import { useCallback } from 'react';
import { activatePortById } from '../../../api';
import { deriveGatewayLabel } from '../utils/portHelpers';

/**
 * Hook for handling port activation
 */
export function usePortActivation(setError, setSuccess) {
  const handleActivate = useCallback(
    async (port) => {
      try {
        if (!port || !port.id) {
          setError('Invalid port selection');
          return;
        }
        if (!port.assigned_number) {
          setError('Port has no assigned number');
          return;
        }

        // Build strict gateway label "gw-<n>"
        const gatewayLabel = deriveGatewayLabel(port);
        if (!gatewayLabel) {
          setError('Cannot determine gateway for this port');
          return;
        }

        setError(null);
        setSuccess(null);

        const res = await activatePortById(
          port.id,
          port.assigned_number,
          gatewayLabel
        );

        const info = res || {};
        if (info.status === 'active') {
          const msg = `Activated now: ${port.assigned_number} on ${port.port_number} (${gatewayLabel})`;
          setSuccess(msg);
        } else if (
          info.status === 'queued' ||
          info.status === 'activating'
        ) {
          const pos = info.position ?? 1;
          const eta = info.eta_seconds ?? info.remaining_seconds ?? 60;
          const msg = `Queued (${pos}) — ETA ~${Math.ceil(
            eta
          )}s for ${port.assigned_number} (${gatewayLabel})`;
          setSuccess(msg);
        } else if (info.duplicate) {
          const pos = info.position ?? 1;
          const eta = info.eta_seconds ?? 30;
          const msg = `Already queued (pos ${pos}, ~${Math.ceil(
            eta
          )}s) (${gatewayLabel})`;
          setSuccess(msg);
        } else {
          setSuccess(`Activation requested (${gatewayLabel})`);
        }
      } catch (err) {
        console.error('Activation error:', err);

        const detail =
          err?.response?.data?.detail ||
          err?.response?.data?.message ||
          err?.message ||
          'Activation failed';

        if (err?.response?.status === 423) {
          setError('Auto-cycle active; manual activations disabled');
        } else if (err?.response?.status === 403) {
          setError('Not allowed to activate this port');
        } else {
          setError(detail);
        }
      }
    },
    [setError, setSuccess]
  );

  return { handleActivate };
}


